import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { INotes, IUser } from '../interfaces';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
const utilityUrl = process.env.REACT_APP_API_UTILITY_URL || '/utility';

interface RawUploadOptions {
  url: string;
  formData: FormData;
  config?: AxiosRequestConfig;
}

interface ApiRequestOptions<TBody = any> {
  url: string;
  method?: AxiosRequestConfig['method'];
  params?: Record<string, any>;
  data?: TBody;
  headers?: Record<string, string>;
  responseType?: AxiosRequestConfig['responseType'];
  config?: AxiosRequestConfig;
}

export const uploadFile = async <T = any>({
  url,
  formData,
  config = {},
}: RawUploadOptions): Promise<T> => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      method: 'POST',
      url: baseUrl + url,                  
      data: formData,    
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(config.headers || {}),
      },
      ...config,
    };

    const response: AxiosResponse<T> = await axios(axiosConfig);
    return response.data;
  } catch (err: any) {
    console.error('Raw upload error:', err);
    throw err.response?.data || err;
  }
};


export const gql = async <T>({
  query,
  variables,
  returnFunc,
}: {
  query: string;
  variables?: Record<string, any>;
  returnFunc: string;
}): Promise<any> => {

  const getFilePaths = (obj: any, path = 'variables'): [string, File | Blob][] => {
    let files: [string, File | Blob][] = [];
    for (const key in obj) {
      const value = obj[key];
      const currentPath = `${path}.${key}`;
      if (value instanceof File || value instanceof Blob) {
        files.push([currentPath, value]);
      } else if (value && typeof value === 'object') {
        files = files.concat(getFilePaths(value, currentPath));
      }
    }
    return files;
  };

  let axiosConfig: AxiosRequestConfig<any>;

  if (variables && getFilePaths(variables).length > 0) {
    const formData = new FormData();
    const operations = JSON.parse(JSON.stringify({ query, variables }));
    const files = getFilePaths(operations);

    files.forEach(([path]) => {
      const keys = path.split('.').slice(1); 
      let obj = operations.variables;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = null;
    });

    formData.append('operations', JSON.stringify(operations));

    const map: Record<string, string[]> = {};
    files.forEach(([path], index) => {
      map[index] = [path];
    });
    formData.append('map', JSON.stringify(map));

    files.forEach(([_, file], index) => {
      formData.append(index.toString(), file);
    });

    axiosConfig = {
      url: baseUrl,
      method: 'POST',
      data: formData
    };

  } else {
    axiosConfig = {
      url: baseUrl,
      method: 'POST',
      data: { query, variables },
      headers: { 'Content-Type': 'application/json' },
    };
  }

  const response: AxiosResponse<{ data: T }> = await axios(axiosConfig);
  return response.data.data[returnFunc as keyof T];
};


export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    getUser: builder.query<IUser[], void>({
      query: () => '/user',
    }),
    mockAuthenticate: builder.mutation<{ token: string }, { username: string; password: string }>({
      query: (credentials) => ({
        url: '/mock-auth',
        method: 'POST',
        body: credentials,
      }),
    }),
    register: builder.mutation<{ token: string }, {
      first_name: string;
      last_name: string;
      status: number;
      organization_id: number;
      role: number;
      level: number;
      job_title?: string;
      email: string;
      phone?: string;
      country?: string;
      address_street_city?: string;
      postal_code?: string;
      photo?: number;
      notes?: INotes[]
    }>({
      query: (credentials) => ({
        url: '/register',
        method: 'POST',
        body: credentials,
      }),
    }),
    
    signOut: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/sign-out',
        method: 'POST',
      }),
    }),

    googleLogin: builder.mutation<{ token: string }, { tokenId: string }>({
      query: ({ tokenId }) => ({
        url: '/auth/google',
        method: 'POST',
        body: { tokenId },
      }),
    }),

    githubLogin: builder.mutation<{ token: string }, { tokenId: string }>({
      query: ({ tokenId }) => ({
        url: '/auth/github',
        method: 'POST',
        body: { tokenId },
      }),
    }),
  }),
});


export const apiRequest = async <
  TResponse = any,
  TBody = any
>({
  url,
  method = 'GET',
  params,
  data,
  headers = {},
  responseType = 'json',
  config = {},
}: ApiRequestOptions<TBody>): Promise<TResponse> => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      url: utilityUrl + url,
      method,
      params,
      data,
      responseType,
      headers: {
        ...headers,
        ...(config.headers || {}),
      },
      ...config,
    };

    const response: AxiosResponse<TResponse> = await axios(axiosConfig);
    return response.data;

  } catch (err: any) {
    throw err.response?.data || err;
  }
};

export const {
  useGetUserQuery,
  useMockAuthenticateMutation,
  useRegisterMutation,
  useSignOutMutation,
  useGoogleLoginMutation,
  useGithubLoginMutation,
} = api;
